/**
 * Figure S5's "Entity map · <Entity> on the portfolio map".
 *
 * The SAME projected layout the Portfolio Atlas draws (GET
 * /whitespace/portfolio_map), read here with one entity in colour and
 * everything else as grey density. It fetches nothing of its own and reveals
 * nothing extra: the layout is already scoped to what this portfolio is
 * entitled to see, and this component only chooses which of those points to
 * colour.
 *
 * WHAT IT DRAWS. The first build plotted bare dots, so the card read as an
 * almost-empty box with six labels stacked on each other. It now draws what
 * the figure draws: a filled footprint per invention of this entity in its own
 * colour, the rest of the book as grey footprints behind them, a centroid star
 * and one label per invention, placed so no two labels overlap.
 *
 * A footprint is a project's centroid plus the spread of its own documents
 * around it (lib/whitespace.computeFootprintEllipse) — not a convex hull, and
 * the card's footer says as much. A project with fewer than two placed
 * documents has no spread to draw, and the card says how many those are
 * rather than quietly leaving them off.
 *
 * NOTHING IS DRAWN FOR AN INVENTION WITHOUT A FOOTPRINT. Round 2 drew a
 * centroid star for any invention with at least one placed document, while a
 * footprint needs two — so a one-document invention appeared as a lone
 * coloured star with no shape, no dots and no label, on a card whose own
 * sentence said it was "not drawn". The star, the dots and the label now all
 * follow the footprint, so the sentence and the picture agree.
 *
 * NO LEADER LINES — A NAME SITS BESIDE ITS OWN MARK. Round 2 stacked the names
 * centre-left across the footprints, dots and stars, so which name belonged to
 * which shape was guesswork; rounds 5-7 answered that with a thin leader line
 * from each name back to its own star. The figure draws no such line — every
 * name in screen-S5-entity.html sits directly above or below its own star, on
 * its own x — and the coordinator's addendum 8/8a rules that no map in this
 * build may draw one, because a dashed line reads as an eighth ray or as a box
 * round somebody else's invention.
 *
 * So the pairing is made by POSITION, using the same placer the S4 atlas uses
 * (components/ownscope/atlasGeometry): a name is placed nearer its own star
 * than anybody else's, beside or below its own mark, clear of every other name
 * and of every shape that is not its own. A name the plot cannot seat beside
 * its mark carries its roster colour as a chip before its first line instead,
 * and the legend says so — the same answer the atlas gives.
 *
 * THE AMBER OVERLAP. The figure carries one amber ellipse over the ground this
 * entity's inventions share with somebody else's, an "overlap · N" chip beside
 * the header chips, and an "overlap" entry in the legend. It is drawn from the
 * REAL overlap groups — the same GET /whitespace/portfolio_overlap groups the
 * Overlap view and the S6 rail card read — scoped to groups that include at
 * least one of this entity's inventions, and its shape is the spread of every
 * placed document belonging to that group's members, which is what "the ground
 * they share" means here. A group below the evidence floor (worth_a_look) is
 * NOT drawn: it is a maybe, and an amber ellipse reads as a finding. Where
 * there is no scored group the card draws nothing and says so — never a
 * placeholder ellipse.
 *
 * The viewer's OWN inventions are never plotted here — they are listed in
 * "Yours — not part of Portfolio" and nowhere else.
 */
import { Fragment } from 'react'
import {
  computeCentroid2D,
  computeFootprintBounds,
  computeFootprintEllipse,
  groupPortfolioLayoutByProject,
  makeAtlasProjector,
  projectTagColor,
} from '../../lib/whitespace'
import {
  NAME_LEADER_CAP,
  NAME_LINE_H,
  NAME_SWATCH,
  boxDistanceTo,
  boxHitsDisc,
  boxHitsEllipse,
  boxesOverlap,
  labelBoxAt,
  placeLabels,
  placeNameChip,
  wrapLabel,
  wrappedLabelSize,
} from '../ownscope/atlasGeometry'
import type { Box, Disc, Ellipse, MeasureText } from '../ownscope/atlasGeometry'
import type { OverlapFootprintEllipse } from '../../lib/whitespace'
import type { OverlapGroup, PortfolioMapLayout } from '../../types/whitespace'

/** Amber, the figure's overlap colour — the same amber the next-step CTA uses. */
const OVERLAP_COLOUR = '#F59E0B'

const VIEWPORT = { width: 640, height: 260, padding: 34 }

export interface EntityMapProps {
  layout: PortfolioMapLayout
  /** Every project on the portfolio map. */
  memberProjectIds: string[]
  /** This entity's granted projects — the ones drawn in colour. */
  entityProjectIds: string[]
  /** project_id -> name, for the label beside each centroid. */
  projectNames: Map<string, string>
  /** project_id -> document count, for "· 12 documents". */
  documentCounts?: Map<string, number>
  /**
   * The portfolio's real overlap groups, straight from
   * GET /whitespace/portfolio_overlap. Only the ones touching this entity are
   * used, and only the scored ones are drawn.
   */
  overlapGroups?: OverlapGroup[]
  entityLabel: string
}

export interface EntityOverlaps {
  /** Scored groups touching this entity — these are drawn amber. */
  scored: OverlapGroup[]
  /** Groups below the evidence floor: named in the line, never drawn. */
  thin: OverlapGroup[]
}

/**
 * The overlap groups that touch this entity's inventions, split by whether
 * they cleared the evidence floor. A `worth_a_look` group is explicitly "thin
 * evidence, not yet a verdict" (the same wording S6's rail card uses), so it
 * never gets an ellipse — an amber shape on a map reads as a finding.
 *
 * Pure and exported so the scoping rule can be tested without a DOM.
 */
export function entityOverlaps(
  groups: OverlapGroup[],
  entityProjectIds: string[],
): EntityOverlaps {
  const mine = new Set(entityProjectIds)
  const touching = groups.filter((g) => g.member_project_ids.some((id) => mine.has(id)))
  return {
    scored: touching.filter((g) => g.worth_a_look !== true),
    thin: touching.filter((g) => g.worth_a_look === true),
  }
}

export const OVERLAP_LABEL_ID = '__overlap__'

/** The plot's own face, so the measurement is of the text that is drawn. */
const LABEL_FONT = (size: number) => `700 ${size}px Inter, system-ui, sans-serif`

/**
 * Per-character advance to fall back on where there is no canvas — jsdom, and
 * any browser that refuses a 2d context. Measured on the deployed plot, first
 * ink to last, across eight labels: 7.29 to 7.80 px per character at this weight and
 * size. 7.5 is the middle of that, and erring wide is the safe direction: a
 * box a little too big moves a label, a box too small CLIPS it.
 */
const FALLBACK_ADVANCE = 7.5

const widthCache = new Map<string, number>()
let measurer: CanvasRenderingContext2D | null | undefined

/**
 * How wide a label really is, at the size it is really drawn.
 *
 * ROUND 6 SHIPPED AN ESTIMATE AND IT CLIPPED FOUR LABELS. The old constant
 * said 5.4px per character; the real advance is 7.29-7.80, so every box was
 * 6-8% short — three or four characters per label — and the solver pushed
 * labels right up to the plot edge believing they fitted. "Autonomous Drone
 * Swarm Coordination · 3 documents" lost its first four letters; two others
 * lost the end of "documents".
 *
 * So it is measured, not estimated: canvas.measureText against the same face
 * the SVG draws, cached per string, with the measured fallback above where no
 * canvas exists.
 */
export function halfWidthFor(text: string, fontSize = 10.5): number {
  const key = `${fontSize}|${text}`
  const hit = widthCache.get(key)
  if (hit !== undefined) return hit
  if (measurer === undefined) {
    try {
      measurer = document.createElement('canvas').getContext('2d')
    } catch {
      measurer = null
    }
  }
  let width = text.length * FALLBACK_ADVANCE
  if (measurer) {
    measurer.font = LABEL_FONT(fontSize)
    const measured = measurer.measureText(text).width
    // A context that answers 0 for everything is not measuring; do not trust it.
    if (measured > 0) width = measured
  }
  const half = Math.max(14, width / 2)
  widthCache.set(key, half)
  return half
}

/**
 * THE NAME LAYOUT — the S4 atlas's placer, not one of this map's own.
 *
 * Round 7 solved the names here with a bespoke scorer whose two hard rules were
 * about LEADER LINES: no leader through a label, no label on another's leader.
 * The coordinator's addendum 8/8a has since ruled that no map in this build
 * draws a leader at all — a dashed line reads as an eighth ray or as a box
 * round somebody else's invention — so a scorer built around leaders was
 * scoring something that is no longer drawn.
 *
 * The rules the S4 atlas already carries are the ones that survive, and they
 * live in components/ownscope/atlasGeometry: a name sits nearer its OWN star
 * than anybody else's (placeLabels' NEARER_ANOTHER_STAR term), beside or below
 * its own mark, clear of every other name and of every shape that is not its
 * own; and a name the map cannot seat beside its mark carries its roster colour
 * as a chip before its first line (placeNameChip) instead of a line back to it.
 * One implementation, two maps, so they cannot drift apart.
 *
 * WHAT THIS FILE STILL OWNS is the MEASUREMENT. The atlas estimates a label's
 * width from its character count at 11.5px/600; this plot draws at 10.5px/700
 * and measures with canvas, where the real advance is 7.29-7.80px per character
 * against the estimate's 6.44. That gap is 15-20%, which is exactly the size of
 * gap that clipped four labels in round 6, so the measured width is injected
 * into the shared placer rather than the placer guessing on its behalf.
 */

/** The widest a name is allowed to run before it wraps to a second line. A
 * whole name over two lines is always readable; a shortened one cannot be
 * matched back to the roster, which is why nothing here truncates. */
const NAME_MAX_WIDTH = 220

/** The size each kind of label is drawn at, so it is measured at that size. */
const NAME_FONT_SIZE = 10.5
const OVERLAP_FONT_SIZE = 9.5

/** The star glyph is a 13px ★; 7px of radius is the ink a name must keep off. */
const STAR_RADIUS = 7

/** The radii the document dots are actually DRAWN at, so the ink a name has to
 * keep off is the ink the reader can see: this entity's own documents at 2.6,
 * the rest of the book's grey density at 1.9. */
const OWN_DOT_RADIUS = 2.6
const OTHER_DOT_RADIUS = 1.9

/** Clear space every label keeps from the edge of the plot band. */
const EDGE_PAD = 4

/** What it costs a name to sit on its OWN footprint — a preference, not a
 * collision, and deliberately below the placer's own OUT_OF_CAP of 5 so a name
 * still prefers to stay beside its mark. It exists to stop the halo rubbing an
 * arc out of the outline the name is standing on. */
const OWN_FOOTPRINT_COST = 3

/** This plot's measured width, in the shape the shared placer asks for. */
export const measureLabel: MeasureText = (text, fontSize) => halfWidthFor(text, fontSize) * 2

/** "Cellmap Assay · 12 documents" — never "1 docs". */
export function mapLabel(name: string, documents: number | undefined): string {
  if (documents === undefined) return name
  return `${name} · ${documents} document${documents === 1 ? '' : 's'}`
}

export default function EntityMap({
  layout,
  memberProjectIds,
  entityProjectIds,
  projectNames,
  documentCounts,
  overlapGroups = [],
  entityLabel,
}: EntityMapProps) {
  const grouped = groupPortfolioLayoutByProject(layout, memberProjectIds)
  const mine = new Set(entityProjectIds)
  const others = memberProjectIds.filter((id) => !mine.has(id))

  const footprintOf = (id: string): OverlapFootprintEllipse | null =>
    computeFootprintEllipse(id, grouped.get(id) ?? [])
  const myFootprints = entityProjectIds
    .map(footprintOf)
    .filter((f): f is OverlapFootprintEllipse => f !== null)
  const otherFootprints = others
    .map(footprintOf)
    .filter((f): f is OverlapFootprintEllipse => f !== null)

  // The ground this entity shares with somebody else: one ellipse over every
  // placed document belonging to a scored group's members. A group whose
  // members have fewer than two placed points between them has no spread to
  // draw and is simply not drawn — the same rule the inventions follow.
  const overlaps = entityOverlaps(overlapGroups, entityProjectIds)
  const overlapFootprints = overlaps.scored
    .map((group) => {
      const points = group.member_project_ids.flatMap((id) => grouped.get(id) ?? [])
      return computeFootprintEllipse(group.group_id, points)
    })
    .filter((f): f is OverlapFootprintEllipse => f !== null)

  const projector = makeAtlasProjector(
    computeFootprintBounds([...myFootprints, ...otherFootprints, ...overlapFootprints]),
    VIEWPORT,
  )
  const toScreen = (f: OverlapFootprintEllipse) => {
    const [cx, cy] = projector([f.cx, f.cy])
    const [ex] = projector([f.cx + f.rx, f.cy])
    const [, ey] = projector([f.cx, f.cy + f.ry])
    return { cx, cy, rx: Math.max(6, Math.abs(ex - cx)), ry: Math.max(6, Math.abs(ey - cy)) }
  }

  // Only an invention WITH a footprint is drawn at all — star, dots and label
  // alike. One with a single document has no spread to draw, and the card's
  // own sentence below says how many those are.
  const drawn = myFootprints.map((f) => ({ footprint: f, screen: toScreen(f) }))
  const drawnIds = new Set(drawn.map((d) => d.footprint.project_id))

  // EVERY NAME, WHOLE, WRAPPED RATHER THAN SHORTENED. A name the reader cannot
  // match back to the roster is not a name, so nothing here truncates: a name
  // too wide for NAME_MAX_WIDTH takes a second line instead, which the placer
  // then has somewhere to put. wrapLabel is the atlas's own, measured with THIS
  // plot's canvas metrics rather than the atlas's 11.5px estimate.
  const nameLabels = drawn.map(({ footprint, screen }) => {
    const name = projectNames.get(footprint.project_id) ?? footprint.project_id
    const text = mapLabel(name, documentCounts?.get(footprint.project_id))
    const lines = wrapLabel(text, NAME_MAX_WIDTH, NAME_FONT_SIZE, measureLabel)
    const { width, height } = wrappedLabelSize(lines, NAME_FONT_SIZE, NAME_LINE_H, measureLabel)
    return {
      key: footprint.project_id,
      // The figure hangs a name under its own shape, so that is where the
      // search starts: the baseline that puts the top of the box 4px clear of
      // the footprint's bottom edge.
      x: screen.cx,
      y: screen.cy + screen.ry + 4 + height * 0.8,
      width,
      height,
      lines,
      fontSize: NAME_FONT_SIZE,
    }
  })
  // The overlap annotation is a LABEL, not a decoration painted afterwards.
  // Round 5 drew it outside the solver and it landed underneath the Modular
  // Battery label, unreadable. It names the ring it is anchored to, and the
  // ring is right there — so, like every other name here, it is placed and not
  // pointed at.
  const overlapRing = overlapFootprints[0] ? toScreen(overlapFootprints[0]) : null
  const overlapLabel = overlapRing
    ? (() => {
      const lines = [`overlap · ${overlaps.scored.length}`]
      const { width, height } = wrappedLabelSize(lines, OVERLAP_FONT_SIZE, NAME_LINE_H, measureLabel)
      return {
        key: OVERLAP_LABEL_ID,
        x: overlapRing.cx,
        y: overlapRing.cy + overlapRing.ry + 4 + height * 0.8,
        width,
        height,
        lines,
        fontSize: OVERLAP_FONT_SIZE,
      }
    })()
    : null
  const allLabels = overlapLabel ? [...nameLabels, overlapLabel] : nameLabels

  // THE INK A NAME MUST KEEP OFF, in the shape atlasGeometry asks for. A label
  // may sit against its OWN shape — the figure puts it there — which is what
  // the `key` on each ellipse and disc buys.
  const labelFootprints: Ellipse[] = [
    ...drawn.map(({ footprint, screen }) => ({ key: footprint.project_id, ...screen })),
    ...overlapFootprints.map((f, i) => ({
      key: i === 0 ? OVERLAP_LABEL_ID : `overlap-${f.project_id}`,
      ...toScreen(f),
    })),
  ]
  // The centroid stars, keyed, which is what makes the pairing rule work: the
  // placer prices any slot that ends up nearer somebody else's star than its
  // own, which is how a reader pairs a name with a mark before anything else.
  const labelStars: Disc[] = drawn.map(({ footprint, screen }) => ({
    key: footprint.project_id,
    cx: screen.cx,
    cy: screen.cy,
    r: STAR_RADIUS,
  }))
  // EVERY DOCUMENT DOT ON THE PLOT — a name may not cover one, whichever mark it
  // belongs to. The atlas ignores its specks on purpose (it draws no halo and
  // its names sit over their own clusters), and rounds 1-2 inherited that. On a
  // plot whose names carry a 2.6px halo a dot is not decoration, it is a datum
  // the halo rubs out: the S5x capture lost a whole purple dot of the CleanTech
  // mark under the word "documents" — 31 strong-purple pixels in the union,
  // zero after — so the picture showed 29 dots while the label said 30. They
  // are drawn at the radii below, and judged at the same 2px clearance as
  // everything else.
  const dotObstacle = (points: [number, number][], r: number): Disc[] =>
    points.map(([x, y]) => {
      const [sx, sy] = projector([x, y])
      return { cx: sx, cy: sy, r }
    })
  const labelDots: Disc[] = [
    ...drawn.flatMap(({ footprint }) =>
      dotObstacle(grouped.get(footprint.project_id) ?? [], OWN_DOT_RADIUS)),
    ...others.flatMap((projectId) =>
      dotObstacle(grouped.get(projectId) ?? [], OTHER_DOT_RADIUS)),
  ]
  const anchors = new Map<string, [number, number]>(
    drawn.map(({ footprint, screen }) => [footprint.project_id, [screen.cx, screen.cy]]),
  )
  if (overlapRing) anchors.set(OVERLAP_LABEL_ID, [overlapRing.cx, overlapRing.cy])

  const placed = placeLabels(
    allLabels,
    {
      footprints: labelFootprints,
      discs: labelStars,
      // A dot is a datum, not a mark: it goes in its own list, never in `discs`,
      // because a keyed disc there is a STAR to the pairing rule and every other
      // project's dot would then count as another invention's mark.
      dots: labelDots,
      anchors,
      // A name stays with its own mark: past this it is priced, and there is no
      // leader to rescue it, so the placer keeps it close.
      cap: NAME_LEADER_CAP,
      edgePad: EDGE_PAD,
      // NO NAME MAY COVER ANY STAR, ITS OWN INCLUDED. The atlas lets a name sit
      // on the mark it names, because the atlas draws no halo. This plot does:
      // every name carries a 2.6px #F8FBFD halo so it reads over the grey
      // density, and a halo ERASES what it covers. Round 1 put
      // "Vibration-Isolated Sensor Mount · 2 documents" straight across its own
      // amber star and rubbed it out — 53 of 142 strong-amber pixels left in the
      // mark's 54x50 box, the only name on the plot with no visible star, and
      // exactly the "name overlapping ink" defect addendum 8 names beside a
      // leader. The star it names is the one piece of ink a name must never be
      // allowed to take.
      ownDiscIsObstacle: true,
      // ...and its own FOOTPRINT is a preference rather than a licence, for the
      // same reason at lower stakes: the S5x capture showed two arc nicks (57px
      // and 48px of erased outline) where a name crossed its own footprint's
      // edge, and the S5 figure draws all three of its names clear of their
      // footprints. Priced below OUT_OF_CAP (5), so a clean slot beside the mark
      // still beats a clear one further away, and a crowded map can still nick
      // rather than fling the name off.
      ownEllipseCost: OWN_FOOTPRINT_COST,
    },
    VIEWPORT,
  )
  const labels = allLabels.map((l) => {
    const at = placed.get(l.key) ?? { x: l.x, y: l.y }
    return { ...l, x: at.x, y: at.y, box: labelBoxAt({ ...l, x: at.x }, at.y) }
  })

  // THE COLOUR CHIP FOR A NAME THIS PLOT COULD NOT PAIR (addendum 8/8a). A name
  // nearer somebody else's star than its own is unpaired, and no leader is
  // allowed to rescue it — so it carries the roster's colour instead, in
  // whichever of the two slots beside its first line is clear. Neither slot
  // fitting is a real answer: a chip clipped by the frame or drawn over another
  // mark is worse than the unpaired name it was meant to rescue, which is why
  // the legend clause is gated on the chips actually rendered.
  const chipClear = (box: Box, ownKey: string) => {
    if (labelFootprints.some((f) => boxHitsEllipse(box, f, 2))) return false
    if (labelStars.some((d) => boxHitsDisc(box, d, 0))) return false
    if (labels.some((n) => n.key !== ownKey && boxesOverlap(box, n.box))) return false
    return true
  }
  const nameChips = new Map<string, { x: number; y: number }>()
  for (const label of labels) {
    if (label.key === OVERLAP_LABEL_ID) continue
    const own = labelStars.find((s) => s.key === label.key)
    if (!own) continue
    const mine = boxDistanceTo(label.box, [own.cx, own.cy])
    const stray = labelStars.some(
      (s) => s.key !== label.key && boxDistanceTo(label.box, [s.cx, s.cy]) < mine,
    )
    if (!stray) continue
    const slot = placeNameChip(
      label,
      (box) => chipClear(box, label.key),
      VIEWPORT,
      EDGE_PAD,
      measureLabel,
      NAME_FONT_SIZE,
    )
    if (slot) nameChips.set(label.key, slot)
  }

  // An invention with fewer than two placed documents has no footprint to
  // draw. Say how many rather than quietly leaving them off the picture.
  const unplaced = entityProjectIds.length - myFootprints.length
  const nothingToDraw = myFootprints.length === 0 && otherFootprints.length === 0

  return (
    <div data-part="EntityMap" className="light-card p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="text-[15px] font-bold text-iflow-900">Entity map</h3>
          <span className="text-[12px] text-gray-500">· {entityLabel} on the portfolio map</span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="rounded-full bg-iflow-100 px-2 py-0.5 font-semibold text-iflow-700">
            In colour · this entity
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600">
            Grey · rest of the book
          </span>
        </div>
      </div>

      {nothingToDraw ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          The portfolio map has not been computed yet, so there is nothing to place {entityLabel} on.
        </p>
      ) : (
        <>
          <div className="mt-3 rounded-lg bg-[#F8FBFD] p-1">
            <svg
              data-testid="entity-map-svg"
              viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`}
              className="w-full"
              style={{ aspectRatio: `${VIEWPORT.width} / ${VIEWPORT.height}` }}
              role="img"
              aria-label={`${entityLabel} plotted against the rest of the portfolio map`}
            >
              {/* The rest of the book, first and underneath: density only. */}
              {otherFootprints.map((f) => {
                const s = toScreen(f)
                return (
                  <ellipse
                    key={`other-${f.project_id}`}
                    data-testid="entity-map-other-footprint"
                    cx={s.cx}
                    cy={s.cy}
                    rx={s.rx}
                    ry={s.ry}
                    fill="#CBD5E1"
                    fillOpacity={0.3}
                  />
                )
              })}
              {others.flatMap((projectId) =>
                (grouped.get(projectId) ?? []).map(([x, y], i) => {
                  const [sx, sy] = projector([x, y])
                  return (
                    <circle key={`${projectId}-${i}`} cx={sx} cy={sy} r={1.9} fill="#94A3B8" opacity={0.5} />
                  )
                }),
              )}

              {/* The shared ground: a hairline dashed RING, never filled. Round
                  4 filled it, and a filled amber shape reads as a sixth
                  footprint — one more invention — rather than as an annotation
                  over the ones already drawn. The count rides on the first
                  ring, beside it, the way the figure prints it: the ellipse
                  itself says what it is, instead of a chip up in the header
                  saying it for something the reader has to go and find. */}
              {overlapFootprints.map((f) => {
                const o = toScreen(f)
                return (
                  <g key={`overlap-${f.project_id}`}>
                    <ellipse
                      data-testid="entity-map-overlap"
                      cx={o.cx}
                      cy={o.cy}
                      rx={o.rx}
                      ry={o.ry}
                      fill="none"
                      stroke={OVERLAP_COLOUR}
                      strokeOpacity={0.9}
                      strokeWidth={0.8}
                      strokeDasharray="3 2"
                    />
                  </g>
                )
              })}

              {/* This entity: a filled footprint per invention, in its colour. */}
              {drawn.map(({ footprint, screen }) => {
                const color = projectTagColor(footprint.project_id, entityProjectIds)
                return (
                  <ellipse
                    key={`mine-${footprint.project_id}`}
                    data-testid={`entity-map-footprint-${footprint.project_id}`}
                    cx={screen.cx}
                    cy={screen.cy}
                    rx={screen.rx}
                    ry={screen.ry}
                    fill={color}
                    fillOpacity={0.16}
                    stroke={color}
                    strokeOpacity={0.7}
                    strokeWidth={1.4}
                  />
                )
              })}
              {entityProjectIds.map((projectId) => {
                // No footprint, nothing drawn — see the card's own sentence.
                if (!drawnIds.has(projectId)) return null
                const pts = grouped.get(projectId) ?? []
                const color = projectTagColor(projectId, entityProjectIds)
                const centroid = computeCentroid2D(pts)
                return (
                  <g key={projectId} data-testid={`entity-map-project-${projectId}`}>
                    {pts.map(([x, y], i) => {
                      const [sx, sy] = projector([x, y])
                      return <circle key={i} cx={sx} cy={sy} r={2.6} fill={color} opacity={0.85} />
                    })}
                    {centroid && (
                      <text
                        x={projector(centroid)[0]}
                        y={projector(centroid)[1] + 4}
                        textAnchor="middle"
                        fontSize="13"
                        fill={color}
                      >
                        ★
                      </text>
                    )}
                  </g>
                )
              })}

              {/* Names last: a name painted under a marker is as unreadable as
                  no name at all. No leader is drawn to any of them — the figure
                  draws none, and addendum 8/8a rules that none may be — so the
                  pairing is the position, and the colour chip where the plot
                  could not manage one. */}
              {labels.map((label) => {
                const isOverlap = label.key === OVERLAP_LABEL_ID
                const firstBaseline = label.lines.length > 1
                  ? label.y - NAME_LINE_H
                  : label.y
                const chip = nameChips.get(label.key)
                return (
                  <Fragment key={label.key}>
                    {chip && (
                      <rect
                        data-name-swatch={label.key}
                        x={chip.x}
                        y={chip.y}
                        width={NAME_SWATCH}
                        height={NAME_SWATCH}
                        rx={2}
                        fill={projectTagColor(label.key, entityProjectIds)}
                        stroke="#F8FBFD"
                        strokeWidth={1}
                      />
                    )}
                    <text
                      data-testid={isOverlap ? 'entity-map-overlap-count' : 'entity-map-label'}
                      data-half-width={label.width / 2}
                      data-label-lines={label.lines.length}
                      x={label.x}
                      y={firstBaseline}
                      textAnchor="middle"
                      fontSize={label.fontSize}
                      fontWeight="700"
                      fill={isOverlap ? OVERLAP_COLOUR : '#0C4A6E'}
                      stroke="#F8FBFD"
                      strokeWidth={isOverlap ? 2.2 : 2.6}
                      paintOrder="stroke"
                    >
                      {label.lines.map((line, i) => (
                        <tspan key={line} x={label.x} dy={i === 0 ? 0 : NAME_LINE_H}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </Fragment>
                )
              })}
            </svg>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10.5px] text-gray-500">
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-iflow-500" aria-hidden /> document
            </span>
            <span>★ centroid</span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-slate-400" aria-hidden /> other entities · density only
            </span>
            {overlaps.scored.length > 0 && (
              <span className="flex items-center gap-1" data-testid="entity-map-overlap-legend">
                <span
                  className="inline-block h-2 w-2 rounded-full border border-amber-500 bg-amber-200"
                  aria-hidden
                />{' '}
                overlap
              </span>
            )}
            {/* The chip is a mark the reader has not seen before, so the legend
                keys it — and only when one is actually drawn. A clause
                describing ink that is not there is the fault this card has
                avoided everywhere else. */}
            {nameChips.size > 0 && (
              <span data-stray-swatch-clause>
                A name that cannot sit beside its mark carries its colour.
              </span>
            )}
          </div>

          {unplaced > 0 && (
            <p className="mt-1 text-[11px] text-gray-500" data-testid="entity-map-unplaced">
              {unplaced} of {entityLabel}&apos;s {entityProjectIds.length} inventions{' '}
              {unplaced === 1 ? 'has' : 'have'} fewer than two documents placed on the map, so{' '}
              {unplaced === 1 ? 'it is' : 'they are'} not drawn.
            </p>
          )}

          {/* The overlap, said plainly whichever way it falls — an amber
              ellipse where there is a real finding, and a sentence where
              there is not. Never a placeholder shape. */}
          <p className="mt-1 text-[11px] text-gray-500" data-testid="entity-map-overlap-line">
            {overlaps.scored.length > 0
              ? `In amber: ${overlaps.scored.length} scored overlap group`
                + `${overlaps.scored.length === 1 ? '' : 's'} `
                + `${overlaps.scored.length === 1 ? 'includes' : 'include'} one of ${entityLabel}'s inventions — `
                + 'the ground it shares with another entity.'
              : overlaps.thin.length > 0
                ? `No overlap is drawn: the ${overlaps.thin.length} group`
                  + `${overlaps.thin.length === 1 ? '' : 's'} touching ${entityLabel} `
                  + `${overlaps.thin.length === 1 ? 'is' : 'are'} worth a look on thin evidence, not a verdict.`
                : `No overlap group includes one of ${entityLabel}'s inventions, so none is drawn.`}
          </p>

          <div className="mt-2 flex flex-wrap justify-between gap-3 text-[11px] italic text-gray-500">
            <span>Positions are a reading, not a measurement · validated in full space</span>
            <span>Nothing fetched beyond the Portfolio view</span>
          </div>
        </>
      )}
    </div>
  )
}
